import { CommonModule } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { SearchComponent } from 'src/app/layouts/search/search.component';
import { CandidateService } from 'src/app/services/candidate.service';
import { environment } from 'src/environments/environment';
import { FormsModule, FormControl, ReactiveFormsModule } from '@angular/forms';
import { GetCandidate } from 'src/app/models/candidate/get-candidate';
import { Paged } from 'src/app/models/shared/paged';
import { GetFreelancerFilter } from 'src/app/models/security/get-user-filter';
import { DropDownKeywords, DropdownOption } from 'src/app/models/shared/dropdown-option';
import { DropdownService } from 'src/app/services/dropdown.service';
import { DropdownComponent } from 'src/app/components/shared/dropdown/dropdown.component';
import { ViewTrackingService, ViewType } from 'src/app/services/view-tracking.service';
import { ViewCountBaseService } from 'src/app/services/view-count-base.service';

@Component({
    selector: 'app-freelancer',
    templateUrl: './freelancer.component.html',
    styles: [],
    standalone: true,
    imports: [ RouterLink, SearchComponent, CommonModule, FormsModule, ReactiveFormsModule, DropdownComponent ]
})
export class FreelancerComponent implements OnInit {
  url = environment.api.url;
  isLoading = false;
  profesionals: any[] = [];
  totalProfessionals = 0;
  recentProfessionals = 0;
  experiencedProfessionals = 0;
  
  // Tracking properties
  ViewType = ViewType;
  viewCounts = signal<{ [key: number]: number }>({});
  
  // Basic UI filters
  uiFilters = {
    minRate: 0,
    maxRate: 0
  };
  
  // API filters for the backend
  apiFilters: GetFreelancerFilter = {
    page: 1,
    pageSize: 50,
    filterKey: null,
    filterValue: null,
    chargeTypeId: null,
    amountFrom: null,
    amountTo: null,
    professionalProfileId: null,
    stateId: null,
    departmentId: null,
    canTravel: null,
    hasVehicleInsurance: null,
    hasWorkPermit: null,
    isBilingual: null,
    hasCertifications: null
  };
  
  // Dropdown options
  chargeTypes: DropdownOption[] = [];
  
  // Form controls
  chargeTypeControl = new FormControl<number | null>(null);

  
  constructor(
    private _candidateService: CandidateService,
    private dropdownService: DropdownService,
    private route: ActivatedRoute,
    private viewTrackingService: ViewTrackingService,
    private viewCountService: ViewCountBaseService
  ) { }

  ngOnInit(): void {
    this.loadDropdownOptions();
    
    // Set up form control subscriptions
    this.chargeTypeControl.valueChanges.subscribe(value => {
      this.apiFilters.chargeTypeId = value;
    });
    
    // Listen for query parameters from search component
    this.route.queryParams.subscribe(params => {
      if (Object.keys(params).length > 0) {
        // Process query parameters
        if (params['professionalProfileId']) {
          this.apiFilters.professionalProfileId = Number(params['professionalProfileId']);
        }
        if (params['stateId']) {
          this.apiFilters.stateId = Number(params['stateId']);
        }
        if (params['departmentId']) {
          this.apiFilters.departmentId = Number(params['departmentId']);
        }
        if (params['chargeTypeId']) {
          const chargeTypeId = Number(params['chargeTypeId']);
          this.apiFilters.chargeTypeId = chargeTypeId;
          this.chargeTypeControl.setValue(chargeTypeId, { emitEvent: false });
        }
      }
      
      this.loadProfessionals();
    });
  }
  
  loadDropdownOptions(): void {
    // Load charge types
    this.dropdownService.GetOptions(DropDownKeywords.ChargeTypes).subscribe({
      next: res => this.chargeTypes = res
    });
  }
  
  loadProfessionals(): void {
    this.isLoading = true;
    
    // Apply min/max rate filters
    this.apiFilters.amountFrom = this.uiFilters.minRate > 0 ? this.uiFilters.minRate : null;
    this.apiFilters.amountTo = this.uiFilters.maxRate > 0 ? this.uiFilters.maxRate : null;
    
    // El chargeTypeId ya está sincronizado con chargeTypeControl vía el valueChanges
    
    this._candidateService.getIndependentProfessionals(this.apiFilters).subscribe({
      next: (response: Paged<GetCandidate>) => {
        this.profesionals = response.items.map((candidate: GetCandidate) => {
          let photoUrl = 'assets/Images/user.png';
          if (candidate.photoFileId) {
            photoUrl = `${this.url}/File/ViewFile/${candidate.photoFileId}`;
          }
          
          return {
            id: candidate.userId,
            resumeId: candidate.publicResumeId,
            foto: photoUrl,
            profesional: candidate.professionalProfileName || 'Profesional Independiente',
            nombreCompleto: `${candidate.firstName || ''} ${candidate.lastName || ''}`,
            experiencia: 'Profesional cualificado',
            price: candidate.publicResumeId ? 'Ver tarifas' : 'Consultar tarifa'
          };
        });
        
        this.totalProfessionals = this.profesionals.length;
        this.recentProfessionals = Math.min(Math.floor(this.profesionals.length * 0.3), this.profesionals.length);
        this.experiencedProfessionals = Math.min(Math.floor(this.profesionals.length * 0.5), this.profesionals.length);
        this.isLoading = false;
        
        // Cargar conteos de vistas después de obtener los profesionales
        this.loadViewCounts();
      },
      error: (error) => {
        console.error('Error al cargar profesionales independientes:', error);
        this.isLoading = false;
      }
    });
  }

  
  
  applyFilters(): void {
    this.loadProfessionals();
  }
  
  clearFilters(): void {
    // Reset UI filters
    this.uiFilters = {
      minRate: 0,
      maxRate: 0
    };
    
    // Reset form controls
    this.chargeTypeControl.setValue(null);
    
    // Reset API filters
    this.apiFilters = {
      page: 1,
      pageSize: 50,
      filterKey: null,
      filterValue: null,
      chargeTypeId: null,
      amountFrom: null,
      amountTo: null,
      professionalProfileId: null,
      stateId: null,
      departmentId: null,
      canTravel: null,
      hasVehicleInsurance: null,
      hasWorkPermit: null,
      isBilingual: null,
      hasCertifications: null
    };
    
    this.loadProfessionals();
  }

  getViewCount(professionalId: number): number {
    return this.viewCountService.getViewCount(this.viewCounts, professionalId);
  }

  loadViewCounts() {
    this.viewCountService.loadViewCounts(this.profesionals, ViewType.IndependentProfile, this.viewCounts);
  }
}
